import React, { useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import AssetConstants from '@progressivebeef/shared/src/constants/AssetConstants';
import { ATTACHMENT_SIZE_LIMIT } from '@progressivebeef/shared/src/constants/AppConstants';
import {
  ATTACHMENT_TYPES,
  RECORD_BINDER_ALLOWED_TYPES,
} from '@progressivebeef/shared/src/constants/FormConstants';
import { showErrorAlert } from '../Alerts';
import localization from '@progressivebeef/shared/src/localization/i18n';
import { getLocale } from '@progressivebeef/shared/src/services/storageService/GlobalData';
import './style.scss';

const MAX_ATTACHMENTS = 3;

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getFileExtension = (fileName) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1
    ? fileName.substring(lastDotIndex + 1).toLowerCase()
    : '';
};

const AttachRecord = ({
  attachments = [],
  onFilesAdded,
  onFileRemove,
  onFilePreview,
  hasError,
}) => {
  const attachInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const remainingSlots = MAX_ATTACHMENTS - attachments.length;
  const canAddMore = remainingSlots > 0;

  const processFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    let filesToProcess = files;
    if (files.length > remainingSlots) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_LIMIT_REACHED);
      filesToProcess = files.slice(0, remainingSlots);
    }

    const newAttachments = [];
    for (const file of filesToProcess) {
      if (file.size > ATTACHMENT_SIZE_LIMIT) {
        showErrorAlert(localization[getLocale()].FILE_SIZE_EXCEEDS_LIMIT);
        continue;
      }
      if (!RECORD_BINDER_ALLOWED_TYPES.includes(file.type)) {
        showErrorAlert(localization[getLocale()].ONLY_PNG_JPG_PDF_SUPPORTED);
        continue;
      }

      const base64 = await readFileAsBase64(file);
      const isPDF = file.type === 'application/pdf';
      newAttachments.push({
        name: file.name,
        base64,
        extension: getFileExtension(file.name),
        type: isPDF ? ATTACHMENT_TYPES.PDF : 'image',
      });
    }

    if (newAttachments.length > 0 && onFilesAdded) {
      onFilesAdded(newAttachments);
    }
  };

  const handleInputChange = (event) => {
    processFiles(event.target.files);
    event.target.value = '';
  };

  const handleButtonClick = () => {
    attachInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    if (!canAddMore) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    if (!canAddMore) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  return (
    <div className="attach-record-wrapper">
      <div className={`attach-record-container ${hasError ? 'has-error' : ''}`}>
        <h3 className="attach-record-title">
          {localization[getLocale()].ATTACH_RECORD}
          <span className="required"> *</span>
        </h3>

        {canAddMore && (
          <div
            className={`attach-record-upload-area ${isDragging ? 'dragging' : ''} ${hasError ? 'has-error' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="attach-record-content">
              <div className="attach-record-icon">
                <img src={AssetConstants.recordDocument} alt="" />
              </div>
              <p className="attach-record-text">
                {localization[getLocale()].CLICK_OR_DRAG_FILE_TO_UPLOAD}
              </p>
              <p className="attach-record-note">
                *{localization[getLocale()].FILE_UPLOAD_MESSAGE}
              </p>
              <button
                type="button"
                className="attach-record-browse-btn"
                onClick={handleButtonClick}
              >
                {localization[getLocale()].BROWSE}
              </button>
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div className="attach-record-file-list">
            {attachments.map((attachment, index) => (
              <div
                className={`attach-record-file-row ${!attachment.isUploading ? 'is-pressable' : ''}`}
                key={
                  attachment.tempId ||
                  attachment.attachmentId ||
                  `${attachment.name}-${index}`
                }
                onClick={
                  !attachment.isUploading && onFilePreview
                    ? () => onFilePreview(attachment)
                    : undefined
                }
              >
                <img
                  className="attach-record-file-icon"
                  src={AssetConstants.recordAttachment}
                  alt=""
                />
                <span className="attach-record-file-name">{attachment.name}</span>
                {attachment.isUploading ? (
                  <span className="attach-record-file-spinner">
                    <Spinner size="sm" color="#32076e" />
                  </span>
                ) : (
                  <button
                    type="button"
                    className="attach-record-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onFileRemove) onFileRemove(index);
                    }}
                  >
                    <i className="fas fa-times" style={{ fontSize: '14px' }}></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <input
          ref={attachInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          accept={RECORD_BINDER_ALLOWED_TYPES.join(', ')}
          onChange={handleInputChange}
        />
      </div>
    </div>
  );
};

export default AttachRecord;
